import type { StatusKey } from "@aeriosiq/ui";

// Status pill mapping for SB3 state values (lyse/v1@6.0.0 + projects/v1@2.0.0).
// Two enums reach this helper:
//
//   * `DataAcquisitionState` (8 values) — single-axis lifecycle including
//     the analyst sub-workflow. Primary consumer: /data-acquisitions Status
//     column and the AnalysisHeader status pill.
//   * `InspectionInstructionState` (9 values) — work-order axis. Retained
//     for any caller that surfaces an instruction directly.
//
// Pre-SB3 values (CANCELLED, REJECT, DONE, NOT_ASSIGNED, etc.) are
// retained as defensive fallbacks in case any legacy data reaches the FE
// during the transition window (the live DB CHECK is still widened until
// the BE-refactor follow-up narrows it). Unknown values → "open".
export function stateToStatusKey(state: string): StatusKey {
  switch (state) {
    // ─── SB3 DataAcquisitionState (single-axis 8 values) ───────────────
    case "CREATED":
      return "open";
    case "PROCESSING":
      return "in_progress";
    case "PRE_SCREENING":
    case "EXPERT_ANALYSIS":
      return "needs_review";
    case "EVALUATION_DONE":
    case "LOCKED":
      return "closed";
    case "ISSUE":
      return "needs_review";
    case "REJECTED":
      return "archived";
    // ─── SB3 InspectionInstructionState (9 values) ─────────────────────
    // CREATED + PRE_SCREENING + EXPERT_ANALYSIS + REJECTED overlap with
    // DataAcquisitionState above (handled by the earlier cases). The
    // additional II-axis values:
    case "SCHEDULED":
      return "open";
    case "ONSITE_EXECUTION":
    case "DATA_TRANSFER":
    case "DATA_PROCESSING":
      return "in_progress";
    case "ANALYSIS":
      return "needs_review";
    case "COMPLETED":
      return "closed";
    case "WITHDRAWN":
      return "archived";
    // ─── Legacy fallbacks (transition window only) ─────────────────────
    case "NOT_ASSIGNED":
    case "NOT_STARTED":
      return "open";
    case "IN_ONSITE_PROGRESS":
    case "DATA_UPLOAD":
    case "IN_INHOUSE_PROGRESS":
    case "IN_PROGRESS":
    case "UPLOADED":
      return "in_progress";
    case "DONE":
      return "closed";
    case "CANCELLED":
    case "CANCELED":
    case "REJECT":
      return "archived";
    default:
      return "open";
  }
}

// Humanize a SCREAMING_SNAKE_CASE state enum for user-facing surfaces.
// `IN_ONSITE_PROGRESS` → "In onsite progress", `EVALUATION_DONE` →
// "Evaluation done".
export function formatInspectionState(state: string): string {
  const lower = state.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
